import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    InputBase,
    InputAdornment,
    Menu,
    MenuItem,
    Avatar,
    CircularProgress
} from '@mui/material';
import { CloseOutlinedIcon, SearchOutlinedIcon } from 'icons/materialIcons';
import { getTeamMembers } from 'team/apis/teamMemberApi';
import PropTypes from 'prop-types';
import avatarPlaceholder from 'images/avatar-cropped.png';

const SelectCardModal = ({ open, onClose, onSelectCard, anchorEl, teamId }) => {
    const [defaultCards, setDefaultCards] = useState([]);
    const [loadingCards, setLoadingCards] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Fetch team members' default cards when modal opens
    useEffect(() => {
        const fetchDefaultCards = async () => {
            if (!open || !teamId) return;

            setLoadingCards(true);
            try {
                const response = await getTeamMembers(teamId, { status: 'active' });
                if (response && response.success && response.members) {
                    // Extract default cards from members
                    const cards = response.members
                        .filter(member => member.defaultCard && member.defaultCard._id)
                        .map(member => ({
                            ...member.defaultCard,
                            memberName: member.user?.fullName || member.fullName || 'Unknown',
                            memberEmail: member.user?.email || member.email || ''
                        }));
                    setDefaultCards(cards);
                } else {
                    setDefaultCards([]);
                }
            } catch (error) {
                console.error('Error fetching team members default cards:', error);
                setDefaultCards([]);
            } finally {
                setLoadingCards(false);
            }
        };

        fetchDefaultCards();
    }, [open, teamId]);

    // Filter cards based on search query
    const filteredCards = useMemo(() => {
        if (!searchQuery.trim()) {
            return defaultCards;
        }
        const query = searchQuery.toLowerCase();
        return defaultCards.filter(card => {
            const fullName = (card.contact_info?.fullName || card.fullName || '').toLowerCase();
            const cardName = (card.cardName || '').toLowerCase();
            const memberName = (card.memberName || '').toLowerCase();
            return fullName.includes(query) || cardName.includes(query) || memberName.includes(query);
        });
    }, [defaultCards, searchQuery]);

    const handleCardSelect = (card) => {
        if (onSelectCard) {
            onSelectCard(card);
        }
        onClose();
    };

    const getCardDisplayName = (card) => {
        return card.contact_info?.fullName || card.fullName || card.cardName || 'Unnamed Card';
    };

    const getCardAvatar = (card) => {
        return card.profilePictureUrl || card.contact_info?.profilePictureUrl || avatarPlaceholder;
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            disableAutoFocusItem
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
            }}
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                    width: {
                        xs: '70vw',
                        sm: '320px',
                        md: '350px'
                    },
                    maxWidth: '350px',
                    maxHeight: '80vh',
                    overflow: 'hidden',
                    marginTop: '12px',
                    padding: '12px',
                    backgroundColor: '#fafafa'
                }
            }}
            MenuListProps={{
                sx: {
                    padding: 0
                }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        cursor: 'default'
                    }
                }
            }}
        >
            {/* Search Bar */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    backgroundColor: 'white',
                    borderRadius: '20px',
                    border: '1px solid #e3e3e3',
                    padding: '8px 16px',
                    marginBottom: '12px',
                    '&:hover': {
                        boxShadow: 2,
                    },
                }}
            >
                <SearchOutlinedIcon
                    sx={{
                        color: '#828282',
                        fontSize: '18px',
                        marginRight: '8px'
                    }}
                />
                <InputBase
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search cards..."
                    sx={{
                        fontSize: '0.7rem',
                        color: '#4f4f4f',
                        '& .MuiInputBase-input::placeholder': {
                            color: '#828282',
                            opacity: 1,
                        },
                    }}
                />
            </Box>

            <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {loadingCards ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', padding: 3 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : filteredCards.length === 0 ? (
                    <Box sx={{ padding: 2, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: '0.875rem', color: '#828282' }}>
                            {searchQuery ? 'No cards found' : 'No cards available'}
                        </Typography>
                    </Box>
                ) : (
                    filteredCards.map((card) => (
                        <MenuItem
                            key={card._id || card.cardId}
                            onClick={() => handleCardSelect(card)}
                            sx={{
                                borderRadius: '8px',
                                marginBottom: '4px',
                                '&:hover': {
                                    backgroundColor: '#f0f0f0'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                <Avatar
                                    src={getCardAvatar(card)}
                                    alt={getCardDisplayName(card)}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: '#e0e0e0',
                                    }}
                                >
                                    {!getCardAvatar(card) && (getCardDisplayName(card)?.charAt(0)?.toUpperCase() || 'C')}
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>
                                        {getCardDisplayName(card)}
                                    </Typography>
                                    {card.memberName && (
                                        <Typography sx={{ fontSize: '0.8rem', color: '#828282', marginTop: '2px' }}>
                                            {card.memberName}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </MenuItem>
                    ))
                )}
            </Box>
        </Menu>
    );
};

SelectCardModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelectCard: PropTypes.func.isRequired,
    anchorEl: PropTypes.object,
    teamId: PropTypes.string.isRequired
};

export default SelectCardModal;

